import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// Clear cached principal (call on logout)
export function clearAuthCache() {
  // No longer caching principal since SWA handles forwarding
}

// Create axios instance
const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Request interceptor to add auth header
client.interceptors.request.use(async (config) => {
  // In development, add a dev user header
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_USER_EMAIL) {
    config.headers['X-Dev-User-Email'] = import.meta.env.VITE_DEV_USER_EMAIL
  }
  // In production, SWA linked backend automatically forwards X-MS-CLIENT-PRINCIPAL
  // No need to manually construct or attach the header
  return config
})

// Track 401 errors to prevent redirect loops
let authFailureCount = 0
let lastAuthFailureTime = 0
const AUTH_FAILURE_THRESHOLD = 3
const AUTH_FAILURE_WINDOW_MS = 10000 // 10 seconds

// Response interceptor for error handling
client.interceptors.response.use(
  (response) => {
    // Reset failure count on successful response
    authFailureCount = 0
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      const now = Date.now()
      
      // Reset counter if outside the window
      if (now - lastAuthFailureTime > AUTH_FAILURE_WINDOW_MS) {
        authFailureCount = 0
      }
      
      authFailureCount++
      lastAuthFailureTime = now
      
      // If we've had multiple 401s in a short window, don't redirect to avoid loop
      if (authFailureCount >= AUTH_FAILURE_THRESHOLD) {
        console.error('Multiple authentication failures detected, stopping redirect loop')
        error.isAuthLoop = true
        return Promise.reject(error)
      }
      
      // Clear auth cache and redirect to login on 401 (first few attempts)
      clearAuthCache()
      window.location.href = '/.auth/login/aad'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  getMe: () => client.get('/auth/me'),
  updateMe: (data) => client.put('/auth/me', data),
  listUsers: () => client.get('/auth/users'),
  getUser: (userId) => client.get(`/auth/users/${userId}`),
  assignRole: (userId, role) => client.post('/auth/users/role', { user_id: userId, role }),
  assignDepartment: (userId, departmentId, role = 'member') =>
    client.post('/auth/users/department', { user_id: userId, department_id: departmentId, role }),
  removeDepartment: (userId, departmentId) =>
    client.delete(`/auth/users/${userId}/department/${departmentId}`),
  getOrganization: () => client.get('/auth/organization'),
  updateOrganization: (data) => client.put('/auth/organization', data),
  syncProfile: () => client.post('/auth/sync-profile'),
  syncAllUsers: () => client.post('/auth/sync-all-users'),
  searchEntra: (q) => client.get('/auth/entra-search', { params: { q } }),
  inviteUser: (data) => client.post('/auth/invite', data),
}

// Projects API
export const projectsApi = {
  list: (params) => client.get('/projects', { params }),
  listMyProjects: () => client.get('/projects', { params: { my_projects: true } }),
  create: (data) => client.post('/projects', data),
  get: (projectId) => client.get(`/projects/${projectId}`),
  update: (projectId, data) => client.put(`/projects/${projectId}`, data),
  delete: (projectId) => client.delete(`/projects/${projectId}`),
  getBoards: (projectId) => client.get(`/projects/${projectId}/boards`),
  // Project members
  getMembers: (projectId) => client.get(`/projects/${projectId}/members`),
  addMember: (projectId, userId, role = 'member') => 
    client.post(`/projects/${projectId}/members`, null, { params: { user_id: userId, role } }),
  updateMember: (projectId, memberId, role) => 
    client.put(`/projects/${projectId}/members/${memberId}`, null, { params: { role } }),
  removeMember: (projectId, memberId) => 
    client.delete(`/projects/${projectId}/members/${memberId}`),
}

// Boards API
export const boardsApi = {
  create: (projectId, data) => client.post(`/boards?project_id=${projectId}`, data),
  get: (boardId) => client.get(`/boards/${boardId}`),
  update: (boardId, data) => client.put(`/boards/${boardId}`, data),
  delete: (boardId) => client.delete(`/boards/${boardId}`),
  createColumn: (boardId, data) => client.post(`/boards/${boardId}/columns`, data),
  updateColumn: (columnId, data) => client.put(`/boards/columns/${columnId}`, data),
  deleteColumn: (columnId) => client.delete(`/boards/columns/${columnId}`),
  reorderColumns: (boardId, columnIds) => client.post(`/boards/${boardId}/columns/reorder`, { column_ids: columnIds }),
}

// Tasks API
export const tasksApi = {
  list: (params) => client.get('/tasks', { params }),
  create: (data) => client.post('/tasks', data),
  get: (taskId) => client.get(`/tasks/${taskId}`),
  update: (taskId, data) => client.put(`/tasks/${taskId}`, data),
  delete: (taskId) => client.delete(`/tasks/${taskId}`),
  move: (taskId, columnId, position) => client.post(`/tasks/${taskId}/move`, { column_id: columnId, position }),
  getActivities: (taskId) => client.get(`/tasks/${taskId}/activities`),
  addComment: (taskId, content) => client.post(`/tasks/${taskId}/comments`, { activity_type: 'comment', content }),
  getBoardTasks: (boardId) => client.get(`/tasks/board/${boardId}`),
  // Dependencies
  getDependencies: (taskId) => client.get(`/tasks/${taskId}/dependencies`),
  addDependency: (taskId, dependsOnTaskId, type = 'blocked_by') =>
    client.post(`/tasks/${taskId}/dependencies`, { depends_on_task_id: dependsOnTaskId, dependency_type: type }),
  removeDependency: (taskId, dependencyId) => client.delete(`/tasks/${taskId}/dependencies/${dependencyId}`),
}

// Departments API
export const departmentsApi = {
  list: (params) => client.get('/departments', { params }),
  create: (data) => client.post('/departments', data),
  get: (departmentId) => client.get(`/departments/${departmentId}`),
  update: (departmentId, data) => client.put(`/departments/${departmentId}`, data),
  delete: (departmentId) => client.delete(`/departments/${departmentId}`),
  getMembers: (departmentId) => client.get(`/departments/${departmentId}/members`),
}

// Reminders API
export const remindersApi = {
  list: (params) => client.get('/reminders', { params }),
  create: (data) => client.post('/reminders', data),
  get: (reminderId) => client.get(`/reminders/${reminderId}`),
  update: (reminderId, data) => client.put(`/reminders/${reminderId}`, data),
  delete: (reminderId) => client.delete(`/reminders/${reminderId}`),
}

// Transcripts API
export const transcriptsApi = {
  list: () => client.get('/transcripts'),
  upload: (data) => client.post('/transcripts', data),
  uploadFile: async (file, title) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('title', title)
    return client.post('/transcripts/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  get: (transcriptId) => client.get(`/transcripts/${transcriptId}`),
  process: (transcriptId) => client.post(`/transcripts/${transcriptId}/process`),
  processAndCreate: (transcriptId, { projectId, newProjectName, createTasks = true }) =>
    client.post(`/transcripts/${transcriptId}/process-and-create`, {
      project_id: projectId || null,
      new_project_name: newProjectName || null,
      create_tasks: createTasks,
    }),
  delete: (transcriptId) => client.delete(`/transcripts/${transcriptId}`),
}

// AI Conversations API
export const aiApi = {
  listConversations: (projectId) => client.get('/ai/conversations', { params: projectId ? { project_id: projectId } : {} }),
  createConversation: (projectId) => client.post('/ai/conversations', { project_id: projectId }),
  getConversation: (conversationId) => client.get(`/ai/conversations/${conversationId}`),
  getMessages: (conversationId) => client.get(`/ai/conversations/${conversationId}/messages`),
  deleteConversation: (conversationId) => client.delete(`/ai/conversations/${conversationId}`),
  bulkDeleteConversations: (conversationIds) => client.post('/ai/conversations/bulk-delete', { conversation_ids: conversationIds }),
  parseFile: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return client.post('/ai/parse-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// Notifications API
export const notificationsApi = {
  list: (params) => client.get('/notifications', { params }),
  unreadCount: () => client.get('/notifications/unread-count'),
  markRead: (notificationId) => client.post(`/notifications/${notificationId}/read`),
  markAllRead: () => client.post('/notifications/read-all'),
}

// Feedback API
export const feedbackApi = {
  submit: (data) => client.post('/feedback', data),
  list: (params) => client.get('/feedback', { params }),
  delete: (feedbackId) => client.delete(`/feedback/${feedbackId}`),
}

// Calendar Export API
export const calendarApi = {
  getMyTasksUrl: (includeDone = false) => 
    `${API_BASE_URL}/calendar/my-tasks.ics${includeDone ? '?include_done=true' : ''}`,
  getProjectUrl: (projectId, includeDone = false) =>
    `${API_BASE_URL}/calendar/project/${projectId}.ics${includeDone ? '?include_done=true' : ''}`,
  getRemindersUrl: () =>
    `${API_BASE_URL}/calendar/reminders.ics`,
  downloadMyTasks: (includeDone = false) =>
    client.get(`/calendar/my-tasks.ics`, { params: { include_done: includeDone }, responseType: 'blob' }),
  downloadProject: (projectId, includeDone = false) =>
    client.get(`/calendar/project/${projectId}.ics`, { params: { include_done: includeDone }, responseType: 'blob' }),
  downloadReminders: () =>
    client.get(`/calendar/reminders.ics`, { responseType: 'blob' }),
}

// Attachments API
export const attachmentsApi = {
  uploadProjectAttachment: async (projectId, file, description = '') => {
    const formData = new FormData()
    formData.append('file', file)
    return client.post(`/attachments/projects/${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: description ? { description } : {},
    })
  },
  listProjectAttachments: (projectId, skip = 0, limit = 50) =>
    client.get(`/attachments/projects/${projectId}`, { params: { skip, limit } }),
  getAttachment: (attachmentId) => client.get(`/attachments/${attachmentId}`),
  deleteAttachment: (attachmentId) => client.delete(`/attachments/${attachmentId}`),
}

export default client
